import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { API } from '../App';
import { Linkedin, Facebook, Instagram, MessageCircle, Music2, Users } from 'lucide-react';

const SOCIAL_META = {
  whatsapp_channel: { label: 'WhatsApp Channel', icon: MessageCircle, color: 'bg-green-500' },
  whatsapp_group:   { label: 'WhatsApp Group',   icon: Users,         color: 'bg-green-600' },
  linkedin:         { label: 'LinkedIn',         icon: Linkedin,      color: 'bg-blue-700' },
  facebook:         { label: 'Facebook',         icon: Facebook,      color: 'bg-blue-600' },
  facebook_group:   { label: 'Facebook Group',   icon: Facebook,      color: 'bg-blue-800' },
  instagram:        { label: 'Instagram',        icon: Instagram,     color: 'bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500' },
  tiktok:           { label: 'TikTok',           icon: Music2,        color: 'bg-black' },
};

export default function CommunityPage() {
  const [data, setData] = useState({ socials: {}, impact_text: '', headline: 'Join Our Community', subheadline: '' });
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    axios.get(`${API}/community`).then(r => setData(r.data)).catch(() => {});
    axios.get(`${API}/branding`).then(r => setBranding(r.data)).catch(() => {});
  }, []);

  const activeSocials = Object.entries(data.socials || {}).filter(([, url]) => !!url);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 max-w-5xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="community-title">Join the Daily UAE Jobs Community</h1>
          <p className="text-gray-600 max-w-3xl mx-auto">{data.subheadline}</p>
        </div>

        {data.impact_text && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-[#CE1126] to-[#009639] text-white text-center" data-testid="community-impact">
            <p className="text-lg font-semibold">{data.impact_text}</p>
          </Card>
        )}

        {activeSocials.length > 0 ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {activeSocials.map(([key, url]) => {
              const meta = SOCIAL_META[key] || { label: key, icon: Users, color: 'bg-gray-500' };
              const Icon = meta.icon;
              return (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`social-${key}`}>
                  <Card className="p-4 flex items-center gap-3 hover:shadow-xl transition-all">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white ${meta.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="font-semibold text-gray-900">{meta.label}</p>
                  </Card>
                </a>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-500 text-sm py-8">Social links will appear here once configured by admin.</p>
        )}

        {branding && (
          <Card className="p-6 mt-8" data-testid="founder-section">
            <div className="grid md:grid-cols-3 gap-6 items-start">
              <div className="flex justify-center md:justify-start">
                <div className="w-40 h-40 rounded-full overflow-hidden bg-gradient-to-br from-[#CE1126] to-[#009639] ring-4 ring-white shadow-xl">
                  {branding.founder_photo_url ? (
                    <img src={branding.founder_photo_url} alt={branding.founder_name}
                      className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-5xl font-bold">
                      {(branding.founder_name || 'SI').split(' ').map(s => s[0]).join('').slice(0, 2)}
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-wider text-[#CE1126] font-semibold mb-1">Meet the Founder</p>
                <h2 className="text-2xl font-bold" data-testid="founder-name">{branding.founder_name}</h2>
                <p className="text-gray-600">{branding.founder_title}</p>
                <p className="text-xs text-gray-500 mb-3">{branding.founder_subtitle}</p>
                <div className="text-sm text-gray-700 whitespace-pre-line mb-4">{branding.founder_bio}</div>
                <div className="flex flex-wrap gap-2">
                  {branding.founder_linkedin && <a href={branding.founder_linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm text-blue-700 hover:bg-blue-50"><Linkedin className="w-3.5 h-3.5" />LinkedIn</a>}
                  {branding.founder_facebook && <a href={branding.founder_facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm text-blue-600 hover:bg-blue-50"><Facebook className="w-3.5 h-3.5" />Facebook</a>}
                  {branding.founder_instagram && <a href={branding.founder_instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm text-pink-600 hover:bg-pink-50"><Instagram className="w-3.5 h-3.5" />Instagram</a>}
                  {branding.founder_tiktok && <a href={branding.founder_tiktok} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm hover:bg-gray-100"><Music2 className="w-3.5 h-3.5" />TikTok</a>}
                </div>
              </div>
            </div>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
